import { useContext, useEffect } from 'react';
import { GlobalContext } from '@/shared/GlobalContext/GlobalContext';
import openAPIErrorNotification from '@/shared/APIErrorNotification/APIErrorNotification';
import { useRouter } from 'next/router';
import getStudentTaskDetail from '@/api/studentDashboard/getStudentTaskDetail.endpoint';
import useCustomHook from '@/hooks/useCustomHook';
import checkExternalToken from '@/api/auth/checkExternalToken.endpoint';
import { SSOContext } from '@/shared/SSOContext/SSOContext';

export default function useStudentTaskDetail() {
  const context = useContext(GlobalContext);
  const ssoContext = useContext(SSOContext);
  const router = useRouter();

  const { state, actions } = useCustomHook({
    defaultState: { loading: true, task: null, taskActivityParams: null },
    actions: {
      loadTask: async () => {
        try {
          const task = await getStudentTaskDetail(router.query.taskId);
          task.taskActivities?.sort((a, b) => {
            return a.order - b.order;
          });
          actions.setState({
            task,
          });
        } catch (e) {
          openAPIErrorNotification({
            title: 'There was an error',
            description: 'There was a problem loading the task.',
            errorFeedback: e.feedbackData,
          });
        }
      },
      checkExternalToken: async () => {
        actions.setState({ loading: true });
        try {
          await checkExternalToken(state.task.data.provider);
          actions.setState({ loading: false });
        } catch (e) {
          if (e?.response?.status == 428) {
            ssoContext.actions.makeRedirectWithWarning({
              provider: 'google',
              action: 'task',
              state,
              isStudent: true,
              moreScopes: [
                'https://www.googleapis.com/auth/classroom.coursework.me',
              ],
              skippable: true,
            });
          } else {
            openAPIErrorNotification({
              title: 'There was an error',
              description:
                "We couldn't communicate properly with Google Classroom",
              errorFeedback: e.feedbackData,
            });
          }
        }
      },
      runFirstIncompleteTaskActivity: () => {
        // TODO For now we just go to the first task activity
        const taskActivity = state.task.taskActivities[0];
        const taskActivityConf = taskActivity.activityConf.miniactivities
          ? taskActivity.activityConf.miniactivities[0]
          : taskActivity.activityConf;

        const taskActivityURL = getTaskActivityURL(state.task.id, {
          ...taskActivity,
          activityConf: taskActivityConf,
        });
        router.push(taskActivityURL);
      },
      goToNextTaskActivity: () => {
        const currentTaskActivityId = state.taskActivityParams.taskActivityId;
        const miniactivityType =
          state.taskActivityParams.miniactivityConf?.miniactivityType;

        const currentTaskActivityIndex = state.task.taskActivities.findIndex(
          (taskActivity) => taskActivity.id == currentTaskActivityId
        );

        if (currentTaskActivityIndex > -1) {
          const currentTaskActivity =
            state.task.taskActivities[currentTaskActivityIndex];
          const currentMiniactivityIndex = miniactivityType
            ? currentTaskActivity.activityConf.miniactivities?.findIndex(
                (miniactivity) =>
                  miniactivity.miniactivityType == miniactivityType
              )
            : null;

          if (
            miniactivityType &&
            currentTaskActivity.activityConf.miniactivities &&
            currentMiniactivityIndex <
              currentTaskActivity.activityConf.miniactivities.length - 1
          ) {
            const taskActivityURL = getTaskActivityURL(state.task.id, {
              ...currentTaskActivity,
              activityConf:
                currentTaskActivity.activityConf.miniactivities[
                  currentMiniactivityIndex + 1
                ],
            });
            router.push(taskActivityURL);
          } else if (
            currentTaskActivityIndex <
            state.task.taskActivities.length - 1
          ) {
            const nextTaskActivity =
              state.task.taskActivities[currentTaskActivityIndex + 1];

            const taskActivityURL = getTaskActivityURL(state.task.id, {
              ...nextTaskActivity,
              activityConf:
                nextTaskActivity.activityConf?.miniactivities?.[0] ||
                nextTaskActivity.activityConf,
            });
            router.push(taskActivityURL);
          }
        }
      },
    },
  });

  useEffect(() => {
    if (
      context.state.loaded &&
      context.state.user?.student &&
      router.query.taskId
    ) {
      actions.loadTask();
    }
  }, [
    context.state.loaded,
    context.state.user?.student?.id,
    router.query.taskId,
  ]);

  useEffect(() => {
    if (
      context.state.loaded &&
      ssoContext.state.loaded &&
      state.task &&
      state.loading
    ) {
      if (
        state.task?.data?.provider &&
        context.state.user?.student?.ssoProviders.some(
          (ssoProvider) => ssoProvider.provider == 'google'
        ) &&
        !ssoContext.state.skipped
      ) {
        actions.checkExternalToken();
      } else {
        actions.setState({ loading: false });
      }
    }
  }, [
    state.task?.id,
    context.state.loaded,
    context.state.user?.student,
    ssoContext.state.loaded,
  ]);

  useEffect(() => {
    if (router.isReady) {
      if (router.query.taskActivityParams) {
        actions.setState({
          taskActivityParams: JSON.parse(router.query.taskActivityParams),
        });
      } else if (state.task?.id && !state.loading) {
        actions.runFirstIncompleteTaskActivity();
      }
    }
  }, [
    router.isReady,
    router.query.taskActivityParams,
    state.task?.id,
    state.loading,
  ]);

  return { state, actions };
}

export function getTaskActivityURL(taskId, taskActivity) {
  const { activityId, activityConf, id: taskActivityId } = taskActivity;

  const miniactivityConf = getMiniactivityConf(activityConf, 'easy');

  const queryParams = {
    taskActivityParams: JSON.stringify({
      taskId,
      activityId,
      taskActivityId,
      miniactivityConf,
    }),
  };
  const queryString = new URLSearchParams(queryParams).toString();

  return `/student/tasks/${taskId}?${queryString}`;
}

function getMiniactivityConf(activityConf) {
  return {
    miniactivityType: activityConf.miniactivityType,
    level: activityConf.options?.level || 'easy',
    themes: activityConf.themes,
  };
}
